import { useEffect, useMemo } from 'react'
import { View } from 'react-native'
import { useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import FormInput from '@ui/forms/Input/FormInput'
import FormInputDate from '@ui/forms/Date/FormInputDate'
import FormSelect from '@ui/forms/Select/FormSelect'
import InputsSection from '@ui/forms/InputsSection'
import FormSection, { FormRow } from './FormSection'
import {
  CofidisMonthlyApplicationFormValues,
  CofidisSectionProps,
} from '../types'
import {
  ContractTypeValues,
  SalaryMonthsValues,
  TemporaryContractTypes,
  WayTypeValues,
} from '../enums'
import { getProfessionRules, PROFESSION_OPTIONS } from '../professionConfig'
import { getCountryOptions } from '../countries'
import Select from '@ui/forms/Select'

export default function EmploymentSection({
  fieldSuccess,
}: CofidisSectionProps) {
  const { t, i18n } = useTranslation(['cofidis'])
  const { control, trigger } =
    useFormContext<CofidisMonthlyApplicationFormValues>()
  const professionCode = useWatch({ control, name: 'professionCode' })
  const contractType = useWatch({ control, name: 'contractType' })
  const contractEndDateRequired =
    contractType != null && TemporaryContractTypes.includes(contractType)
  const professionRules = useMemo(
    () => getProfessionRules(professionCode),
    [professionCode]
  )
  const contractTypeRequired = professionRules?.contractType === 'required'
  const contractStartDateRequired =
    professionRules?.contractStartDate === 'required'
  const salaryRequired = professionRules?.salary === 'required'
  const salaryMonthsRequired = salaryRequired

  const contractTypeOptions = useMemo(
    () =>
      ContractTypeValues.map((option) => ({
        value: option,
        label: t(`cofidis:contractType.options.${option}.label`),
      })),
    [t]
  )
  const salaryMonthsOptions = useMemo(
    () =>
      SalaryMonthsValues.map((option) => ({
        value: option,
        label: t(`cofidis:salaryMonthsNumber.options.${option}.label`),
      })),
    [t]
  )
  const professionOptions = useMemo(
    () =>
      PROFESSION_OPTIONS.map((option) => ({
        ...option,
        label: t(`cofidis:${option.label}`),
      })),
    [t]
  )
  const wayTypeOptions = useMemo(
    () =>
      WayTypeValues.map((value) => ({
        value,
        label: t(`cofidis:wayType.options.${value}.label`),
      })),
    [t]
  )

  const countryOptions = useMemo(
    () => getCountryOptions(i18n.resolvedLanguage ?? i18n.language ?? 'en'),
    [i18n.resolvedLanguage, i18n.language]
  )

  useEffect(() => {
    if (contractType) {
      trigger('contractStartDate')
    }
  }, [contractType, trigger])

  return (
    <View className="flex flex-col gap-8">
      <InputsSection
        group
        // divider
        // title={t('cofidis:sections.income.header.title')}
        // desc={t('cofidis:sections.income.header.desc')}
      >
        <FormSection>
          <FormRow>
            <FormSelect
              required
              labelVariant="top"
              control={control}
              name="professionCode"
              isSuccess={fieldSuccess?.professionCode}
              label={t('cofidis:professionCode.label')}
              options={professionOptions}
            />
          </FormRow>
          <FormRow>
            <FormSelect
              required={contractTypeRequired}
              labelVariant="top"
              control={control}
              name="contractType"
              isSuccess={fieldSuccess?.contractType}
              label={t('cofidis:contractType.label')}
              options={contractTypeOptions}
            />
          </FormRow>
          <FormRow>
            <FormInputDate
              required={contractStartDateRequired}
              labelVariant="top"
              name="contractStartDate"
              isSuccess={fieldSuccess?.contractStartDate}
              label={t('cofidis:contractStartDate.label')}
            />
            <FormInputDate
              required={contractEndDateRequired}
              labelVariant="top"
              name="contractEndDate"
              isSuccess={fieldSuccess?.contractEndDate}
              label={t('cofidis:contractEndDate.label')}
            />
          </FormRow>
          <FormRow>
            <FormInput
              required={salaryRequired}
              labelVariant="top"
              name="salary"
              isSuccess={fieldSuccess?.salary}
              label={t('cofidis:salary.label')}
              inputMode="decimal"
              next="salaryMonthsNumber"
              textEnd={t('cofidis:salary.end')}
            />
            <FormSelect
              required={salaryMonthsRequired}
              labelVariant="top"
              control={control}
              name="salaryMonthsNumber"
              isSuccess={fieldSuccess?.salaryMonthsNumber}
              label={t('cofidis:salaryMonthsNumber.label')}
              options={salaryMonthsOptions}
            />
          </FormRow>
        </FormSection>
      </InputsSection>

      <InputsSection
        group
        divider
        title={t('cofidis:sections.company.header.title')}
        desc={t('cofidis:sections.company.header.desc')}
      >
        <FormSection>
          <FormRow>
            <FormInput
              required={false}
              labelVariant="top"
              name="companyName"
              isSuccess={fieldSuccess?.companyName}
              label={t('cofidis:companyName.label')}
              autoCapitalize="words"
              next="companyWayType"
            />
          </FormRow>
          <FormRow>
            <FormSelect
              required={false}
              labelVariant="top"
              control={control}
              name="companyWayType"
              isSuccess={fieldSuccess?.companyWayType}
              label={t('cofidis:companyWayType.label')}
              options={wayTypeOptions}
            />
            <FormInput
              required={false}
              labelVariant="top"
              name="companyWayDescription"
              isSuccess={fieldSuccess?.companyWayDescription}
              label={t('cofidis:companyWayDescription.label')}
              autoCapitalize="words"
              next="companyWayNumber"
            />
            <FormInput
              required={false}
              labelVariant="top"
              name="companyWayNumber"
              isSuccess={fieldSuccess?.companyWayNumber}
              label={t('cofidis:companyWayNumber.label')}
              next="companyZipCode"
            />
          </FormRow>
          <FormRow>
            <FormInput
              required={false}
              labelVariant="top"
              name="companyZipCode"
              isSuccess={fieldSuccess?.companyZipCode}
              label={t('cofidis:companyZipCode.label')}
              next="companyTown"
            />
            <FormInput
              required={false}
              labelVariant="top"
              name="companyTown"
              isSuccess={fieldSuccess?.companyTown}
              label={t('cofidis:companyTown.label')}
              autoCapitalize="words"
              next="companyPhone"
            />
            <Select
              onChange={() => ({})}
              disabled
              required={false}
              labelVariant="top"
              label={t('cofidis:companyCountry.label')}
              options={countryOptions}
              value={'ES'} // TODO: verify me
            />
          </FormRow>
          <FormRow>
            <FormInput
              required={false}
              labelVariant="top"
              name="companyPhone"
              isSuccess={fieldSuccess?.companyPhone}
              label={t('cofidis:companyPhone.label')}
              inputMode="tel"
            />
          </FormRow>
        </FormSection>
      </InputsSection>
    </View>
  )
}
